/**
 * Armstrong Music - Collection Page Finish Swatches v8
 * =====================================================
 * Changes from v7:
 * - Skips .sale-overlay img during swap (fixes headstock silhouette flash)
 * - Updates SKU text node when swatch is clicked
 * - Updates product links to include ?variant=ID so click-through
 *   lands on the correct finish variant
 * - Removed blockCardLinks() as it's no longer needed
 */

(function () {
  'use strict';

  const FINISHES = {
    'chrome': {
      label: 'Chrome',
      bg: 'radial-gradient(circle at 35% 30%, #ffffff 0%, #d8d8e0 30%, #a0a0b0 65%, #707080 100%)'
    },
    'nickel': {
      label: 'Nickel',
      bg: 'radial-gradient(circle at 35% 30%, #f5f5e0 0%, #d0d0a8 30%, #a0a078 65%, #707050 100%)'
    },
    'vintage nickel': {
      label: 'Vintage Nickel',
      bg: 'radial-gradient(circle at 35% 30%, #e8e8d8 0%, #c0c0a8 30%, #909078 65%, #606050 100%)'
    },
    'cosmo': {
      label: 'Cosmo Black',
      bg: 'radial-gradient(circle at 35% 30%, #b0b0c0 0%, #606070 30%, #383840 65%, #202025 100%)'
    },
    'black chrome': {
      label: 'Cosmo Black',
      bg: 'radial-gradient(circle at 35% 30%, #b0b0c0 0%, #606070 30%, #383840 65%, #202025 100%)'
    },
    'black chrome (cosmo)': {
      label: 'Cosmo Black',
      bg: 'radial-gradient(circle at 35% 30%, #b0b0c0 0%, #606070 30%, #383840 65%, #202025 100%)'
    },
    'cosmo (black chrome)': {
      label: 'Cosmo Black',
      bg: 'radial-gradient(circle at 35% 30%, #b0b0c0 0%, #606070 30%, #383840 65%, #202025 100%)'
    },
    'cosmo black': {
      label: 'Cosmo Black',
      bg: 'radial-gradient(circle at 35% 30%, #b0b0c0 0%, #606070 30%, #383840 65%, #202025 100%)'
    },
    'black': {
      label: 'Black',
      bg: 'radial-gradient(circle at 35% 30%, #707070 0%, #383838 30%, #181818 65%, #080808 100%)'
    },
    'gold': {
      label: 'Gold',
      bg: 'radial-gradient(circle at 35% 30%, #fff0a0 0%, #d4a830 30%, #a07820 65%, #705010 100%)'
    },
    'aged': {
      label: 'Aged',
      bg: 'radial-gradient(circle at 35% 30%, #d8c888 0%, #b09040 30%, #806820 65%, #504010 100%)'
    },
    'antique': {
      label: 'Antique',
      bg: 'radial-gradient(circle at 35% 30%, #d0c090 0%, #a88840 30%, #785820 65%, #483010 100%)'
    },
    'relic chrome': {
      label: 'Relic Chrome',
      bg: 'radial-gradient(circle at 35% 30%, #e0e0d8 0%, #b8b8a8 30%, #888878 65%, #585850 100%)'
    },
  };

  function detectFinish(variantTitle) {
    const full = variantTitle.toLowerCase().trim();
    if (FINISHES.hasOwnProperty(full)) {
      return { key: full, ...FINISHES[full] };
    }
    const parts = full.split('/').map(p => p.trim());
    for (const part of parts) {
      if (FINISHES.hasOwnProperty(part)) {
        return { key: part, ...FINISHES[part] };
      }
    }
    return null;
  }

  function formatPrice(priceInt) {
    return '£' + (priceInt / 100).toFixed(2);
  }

  // ----------------------------------------------------------
  // Build correctly sized Shopify CDN URL.
  // Variant image srcs from the API look like:
  //   //cdn.shopify.com/.../image.jpg?v=1699536395
  // We just append &width=533 to get the right size.
  // Handles the edge case where there's no query string too.
  // ----------------------------------------------------------
  function shopifyImageUrl(src, width) {
    width = width || 533;
    if (src.indexOf('?') !== -1) {
      return src + '&width=' + width;
    } else {
      return src + '?width=' + width;
    }
  }

  function findCardElements(container) {
    const card = container.closest('.card-wrapper, .product-card-wrapper');
    if (!card) return null;
    const price = card.querySelector('.price-item--regular');
    return { card, price };
  }

  function swapImages(card, newSrc) {
    const imgs = card.querySelectorAll('.card__media img');
    imgs.forEach(function (img) {
      // Skip the headstock silhouette overlay - leave it completely untouched
      if (img.closest('.sale-overlay')) return;

      img.classList.remove('lazyload', 'lazyloaded', 'lazyloading');
      img.removeAttribute('srcset');
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.classList.add('arm-swapped');
      img.src = newSrc;
    });
  }

  function initSwatchContainer(container) {
    if (container.dataset.armInit) return;
    container.dataset.armInit = '1';

    let variants;
    try {
      variants = JSON.parse(container.dataset.variants);
    } catch (e) {
      return;
    }

    if (!variants || variants.length <= 1) return;

    const seen = new Set();
    const finishVariants = [];

    for (const variant of variants) {
      const finish = detectFinish(variant.title);
      if (finish && !seen.has(finish.key)) {
        seen.add(finish.key);
        finishVariants.push({ finish, variant });
      }
    }

    if (finishVariants.length < 2) return;

    const elements = findCardElements(container);

    finishVariants.forEach(({ finish, variant }) => {
      const swatch = document.createElement('span');
      swatch.className = 'arm-swatch';
      swatch.title = finish.label;
      swatch.style.background = finish.bg;
      swatch.setAttribute('aria-label', finish.label);
      swatch.setAttribute('role', 'button');
      swatch.setAttribute('tabindex', '0');

      swatch.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        if (!elements) return;

        // Swap the product image
        if (variant.featured_image && variant.featured_image.src) {
          const newSrc = shopifyImageUrl(variant.featured_image.src, 533);
          swapImages(elements.card, newSrc);
        }

        // Update the price
        if (elements.price && variant.price !== undefined) {
          elements.price.textContent = formatPrice(variant.price);
        }

        // Update the SKU - it's a bare text node sitting after .visually-hidden
        const skuSpans = elements.card.querySelectorAll('.visually-hidden');
        skuSpans.forEach(function (span) {
          if (span.textContent.trim() === 'SKU:') {
            const textNode = span.nextSibling;
            if (textNode && textNode.nodeType === 3 && variant.sku) {
              textNode.textContent = 'Code:' + variant.sku;
            }
          }
        });

        // Update all product links to land on the selected variant
        const links = elements.card.querySelectorAll('a[href*="/products/"]');
        links.forEach(function (a) {
          const base = a.href.split('?')[0];
          a.href = base + '?variant=' + variant.id;
        });
      });

      swatch.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          swatch.click();
        }
      });

      container.appendChild(swatch);
    });
  }

  function init() {
    document.querySelectorAll('.arm-swatch-container').forEach(initSwatchContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.classList && node.classList.contains('arm-swatch-container')) {
          initSwatchContainer(node);
        }
        if (node.querySelectorAll) {
          node.querySelectorAll('.arm-swatch-container').forEach(initSwatchContainer);
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });

})();
